import React from 'react';
import { motion } from 'motion/react';
import { ChevronRight, Shield, Zap, Anchor, Globe, Moon } from 'lucide-react';

const milestones = [
  {
    level: "LEVEL 01",
    title: "SYNDICATE RECRUIT",
    focus: "Basic Handling + Safety Protocols",
    icon: <Shield className="h-4 w-4" />,
    difficulty: {
      color: "green",
      label: "Green Level // Routine",
      hex: "bg-green-500",
      shadow: "shadow-[0_0_10px_rgba(34,197,94,0.3)]",
      border: "border-green-500/20",
      text: "text-green-700",
      pillBg: "bg-green-50",
      req: "Open to All"
    }
  },
  {
    level: "LEVEL 02",
    title: "OPEN WATER STEWARD",
    focus: "Weekend Guided Endurance Training",
    icon: <Anchor className="h-4 w-4" />,
    difficulty: {
      color: "blue",
      label: "Blue Level // Elevated",
      hex: "bg-blue-500",
      shadow: "shadow-[0_0_10px_rgba(59,130,246,0.3)]",
      border: "border-blue-500/20",
      text: "text-blue-700",
      pillBg: "bg-blue-50",
      req: "Open to All"
    }
  },
  {
    level: "LEVEL 03",
    title: "NIGHT CROSSING ELIGIBLE",
    focus: "Navigation, Lighting & Survival Systems",
    icon: <Moon className="h-4 w-4" />,
    difficulty: {
      color: "amber",
      label: "Brown Level // Adverse",
      hex: "bg-amber-600",
      shadow: "shadow-[0_0_10px_rgba(217,119,6,0.3)]",
      border: "border-amber-600/20",
      text: "text-amber-800",
      pillBg: "bg-amber-50",
      req: "OWS Clearance Certificate"
    }
  },
  {
    level: "LEVEL 04",
    title: "MARATHON STRIKER",
    focus: "55km+ Lake Ontario Crossings",
    icon: <Zap className="h-4 w-4" />,
    difficulty: {
      color: "black",
      label: "Black Level // Challenger",
      hex: "bg-gray-800",
      shadow: "shadow-[0_0_10px_rgba(31,41,55,0.3)]",
      border: "border-gray-800/20",
      text: "text-gray-900",
      pillBg: "bg-gray-100",
      req: "OWS Clearance Certificate"
    }
  },
  {
    level: "LEVEL 05",
    title: "GLOBAL EXPEDITIONARY",
    focus: "Baja, Mexico & International Missions",
    icon: <Globe className="h-4 w-4" />,
    difficulty: {
      color: "red",
      label: "Red Level // Apex",
      hex: "bg-red-600",
      shadow: "shadow-[0_0_10px_rgba(220,38,38,0.3)] animate-pulse",
      border: "border-red-600/20",
      text: "text-red-700",
      pillBg: "bg-red-50",
      req: "OWS Clearance Certificate"
    }
  }
];

const ProgressionPathway: React.FC = () => {
  return (
    <section className="py-24 bg-white text-[#141414] overflow-hidden relative" id="progression">
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-ows-teal/5 blur-[120px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 relative">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
          <div>
            <span className="text-[10px] font-mono font-bold uppercase tracking-[0.4em] text-ows-teal">RANK_ASCENSION</span>
            <h2 className="text-4xl md:text-6xl font-display font-light uppercase tracking-tight mt-2 italic shadow-text text-[#141414]">
              THE OWS <br />
              <span className="italic-serif">PATHWAY.</span>
            </h2>
          </div>
          <div className="max-w-md">
            <p className="text-sm font-mono text-[#141414]/40 uppercase tracking-tighter leading-relaxed">
              OWS is not just paddleboarding. It is a transformation system. Earn your rank through discipline, endurance, and technical mastery of the open water.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {milestones.map((milestone, index) => (
            <motion.div 
              key={index}
              initial={{ x: index % 2 === 0 ? -50 : 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="flex items-center gap-6 group cursor-default"
            >
              <div className="hidden md:flex flex-col items-center">
                <div className="w-px h-8 bg-ows-teal/20" />
                <div className="w-8 h-8 rounded-full border border-ows-teal/40 flex items-center justify-center group-hover:bg-ows-teal group-hover:border-ows-teal transition-all duration-500">
                  <div className="w-1.5 h-1.5 bg-ows-teal rounded-full group-hover:bg-ows-white" />
                </div>
                {index === milestones.length - 1 ? null : <div className="w-px h-24 bg-ows-teal/20" />}
              </div>

              <div className="flex-1 bg-[#141414]/5 border border-[#141414]/10 p-6 md:p-8 rounded-2xl hover:bg-white hover:border-ows-teal/50 transition-all duration-500 group shadow-sm hover:shadow-xl hover:shadow-ows-teal/5">
                <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative">
                  
                  <div className="space-y-1 xl:w-1/3">
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-mono font-bold text-ows-teal">{milestone.level}</span>
                      <span className="text-ows-teal">{milestone.icon}</span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-display font-light uppercase tracking-widest text-[#141414]">
                      {milestone.title}
                    </h3>
                  </div>

                  {/* Operational Difficulty Level */}
                  <div className="xl:w-1/3">
                    <div className={`inline-flex items-center gap-4 px-4 py-2.5 rounded-xl border ${milestone.difficulty.border} ${milestone.difficulty.pillBg} transition-all duration-500 group-hover:bg-white`}>
                       <span className={`h-2.5 w-2.5 rounded-full ${milestone.difficulty.hex} ${milestone.difficulty.shadow}`}></span>
                       <div>
                         <div className={`text-[10px] font-bold tracking-widest uppercase ${milestone.difficulty.text}`}>
                           {milestone.difficulty.label}
                         </div>
                         <div className={`text-[9px] font-mono tracking-tighter uppercase mt-0.5 font-medium ${milestone.difficulty.text} opacity-80 flex items-center gap-1.5`}>
                           {milestone.difficulty.req === 'OWS Clearance Certificate' && (
                              <Shield className="h-3 w-3" />
                           )}
                           {milestone.difficulty.req}
                         </div>
                       </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-start xl:justify-end gap-4 xl:w-1/3">
                    <p className="text-[10px] md:text-sm font-mono text-[#141414]/40 uppercase tracking-tighter xl:text-right">
                      {milestone.focus}
                    </p>
                    <ChevronRight className="h-5 w-5 text-ows-teal opacity-0 group-hover:opacity-100 group-hover:translate-x-2 transition-all hidden xl:block" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProgressionPathway;
